import { SearchBuilder } from "@/builder";
import { RequestQueryParams } from "@/models/class/request-query-params.class";
import { RequestQueryParamsModel } from "@/models/interface/http.interface";
import { invoiceServices } from "@/services/invoice.service";
import store from "@/store/auth.store";
import { Moment } from "moment";
import { useContactData } from "./contact";
import useAsset from "./useAsset";
import useAssetCategory from "./useAssetCategory";
import useBranch from "./useBranch";
import useDate from "./useDate";

type FilterField = Partial<{
  branchId: string | null;
  unitCodeId: string | null;
  invoiceDate: Array<Moment> | null;
  rwoDate: Array<Moment> | null;
  poDate: Array<Moment> | null;
  customerId: string | null;
  customerLocation: string | null;
  assetCategoryId: string | null;
}>;

const useUnitMargin = () => {
  const { toDefaultFormat } = useDate();
  const { findAll: findBranch } = useBranch();
  const { findById: findContactById } = useContactData();
  const { findAllMasterAsset } = useAsset();

  const findAll = (params: RequestQueryParamsModel) => {
    return invoiceServices.getListReportMarginUnit(params);
  };

  const download = (params: RequestQueryParamsModel) => {
    return invoiceServices.downloadReportMarginUnit(params);
  };

  const buildDownloadParam = async ({
    branchId,
    unitCodeId,
    invoiceDate,
    rwoDate,
    poDate,
    customerId,
    customerLocation,
    assetCategoryId,
  }: FilterField): Promise<string> => {
    const company = store.state.authData.companyName;
    const unitLocation = customerLocation || "ALL";

    let branch = "ALL";
    if (branchId) {
      const { data } = await findBranch({ search: "secureId~" + branchId });
      branch = data[0].name;
    }

    let custName = "ALL";
    if (customerId) {
      const response = await findContactById(customerId);
      custName = response.firstName;
    }

    let unitCode = "ALL";
    if (unitCodeId) {
      const response = await findAllMasterAsset({
        search: "secureId~" + unitCodeId,
      });
      unitCode = response.data[0].unitCode;
    }

    let invoiceDateFrom = "ALL";
    let invoiceDateTo = "ALL";
    if (invoiceDate && invoiceDate.length) {
      const [start, end] = invoiceDate;
      invoiceDateFrom = toDefaultFormat(start);
      invoiceDateTo = toDefaultFormat(end);
    }

    let rwoDateFrom = "ALL";
    let rwoDateTo = "ALL";
    if (rwoDate && rwoDate.length) {
      const [start, end] = rwoDate;
      rwoDateFrom = toDefaultFormat(start);
      rwoDateTo = toDefaultFormat(end);
    }

    let poDateFrom = "ALL";
    let poDateTo = "ALL";
    if (poDate && poDate.length) {
      const [start, end] = poDate;
      poDateFrom = toDefaultFormat(start);
      poDateTo = toDefaultFormat(end);
    }

    const params: string[] = [
      company,
      branch,
      custName,
      assetCategoryId || "All",
      unitCode,
      invoiceDateFrom,
      invoiceDateTo,
      rwoDateFrom,
      rwoDateTo,
      poDateFrom,
      poDateTo,
      unitLocation,
    ];

    return params.join(",");
  };

  const filterBy = ({
    branchId,
    unitCodeId,
    invoiceDate,
    rwoDate,
    poDate,
    customerId,
    customerLocation,
    assetCategoryId,
  }: FilterField): RequestQueryParamsModel => {
    const builder = new SearchBuilder();
    const params = new RequestQueryParams();

    if (customerId) {
      params.customerId = customerId;
    }

    if (customerLocation) {
      params.customerLocation = customerLocation;
    }

    if (assetCategoryId) {
      params.unitCategory = assetCategoryId;
    }
    
    if (branchId) {
      params.branchId = branchId;
    }
    
    if (unitCodeId) {
      params.assetId = unitCodeId;
    }
    
    if (invoiceDate && invoiceDate.length) {
      const [start, end] = invoiceDate;
      params.invoiceStartDate = start.format();
      params.invoiceEndDate = end.format();
    }

    if (rwoDate && rwoDate.length) {
      const [start, end] = rwoDate;
      params.rwoStartDate = start.format();
      params.rwoEndDate = end.format();
    }

    if (poDate && poDate.length) {
      const [start, end] = poDate;
      params.poStartDate = start.format();
      params.poEndDate = end.format();
    }

    return params;
  };

  return { findAll, download, filterBy, buildDownloadParam };
};

export default useUnitMargin;
